import React from 'react'

export type StepUiState =
  | 'completed' | 'running' | 'awaiting_input'
  | 'current_head' | 'blocked' | 'not_run' | 'rerun_required'

export interface StepInfo {
  step: number
  name: string
  state: StepUiState
  outputVersion: number
  feedbackCount: number
  feedbackPendingRerun: boolean
  accepted: boolean
  feedbackSubmitting?: boolean
}

const STATE_CFG: Record<StepUiState, { label: string; circle: string; icon: React.ReactNode }> = {
  completed: {
    label: 'Done', circle: 'bg-emerald-500 border-emerald-500 text-white',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />,
  },
  running: {
    label: 'Running', circle: 'bg-blue-500 border-blue-500 text-white animate-pulse',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 3a9 9 0 1 0 9 9" strokeOpacity={0.4} />,
  },
  awaiting_input: {
    label: 'Needs Input', circle: 'bg-amber-500 border-amber-500 text-white',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01" />,
  },
  current_head: {
    label: 'Ready', circle: 'bg-cyan-500 border-cyan-500 text-white',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M5 3l14 9-14 9V3z" />,
  },
  blocked: {
    label: 'Locked', circle: 'bg-white border-slate-300 text-slate-400',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />,
  },
  not_run: {
    label: 'Not Started', circle: 'bg-white border-slate-300 text-slate-400',
    icon: <circle cx="12" cy="12" r="3" />,
  },
  rerun_required: {
    label: 'Needs Rerun', circle: 'bg-amber-400 border-amber-400 text-white',
    icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />,
  },
}

const lineColor = (leftState: StepUiState, rightState: StepUiState) => {
  if (leftState === 'completed' && rightState === 'completed') return 'bg-emerald-400'
  if (leftState === 'completed') return 'bg-gradient-to-r from-emerald-400 to-slate-200'
  return 'bg-slate-200'
}

interface StepNavigatorProps {
  steps: StepInfo[]
  currentStep: number
  onSelect: (step: number) => void
  branchLabel?: string | null
}

const LABEL_TEXT: Record<StepUiState, string> = {
  completed: 'text-emerald-700',
  running: 'text-blue-700',
  awaiting_input: 'text-amber-700',
  current_head: 'text-cyan-700',
  blocked: 'text-slate-400',
  not_run: 'text-slate-400',
  rerun_required: 'text-amber-700',
}

export const StepNavigator: React.FC<StepNavigatorProps> = ({ steps, currentStep, onSelect, branchLabel }) => {
  const doneCount = steps.filter((s) => s.state === 'completed').length
  const donePct = steps.length ? (doneCount / steps.length) * 100 : 0

  return (
    <div className="bp-card px-4 py-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pipeline Progress</h3>
          {branchLabel && (
            <span className="truncate rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700">
              {branchLabel}
            </span>
          )}
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          <div className="hidden h-1 w-20 overflow-hidden rounded-full bg-slate-100 sm:block">
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${donePct}%` }} />
          </div>
          <span className="text-[11px] font-medium tabular-nums text-slate-500">
            {doneCount}/{steps.length} done
          </span>
        </div>
      </div>

      <div className="-mx-1 overflow-x-auto px-1">
        <div className="flex min-w-max items-center lg:min-w-0">
          {steps.map((step, idx) => {
            const cfg = STATE_CFG[step.state]
            const isSelected = currentStep === step.step
            const isLast = idx === steps.length - 1

            return (
              <React.Fragment key={step.step}>
                <button
                  type="button"
                  onClick={() => onSelect(step.step)}
                  aria-current={isSelected ? 'step' : undefined}
                  title={`Step ${step.step}: ${step.name}`}
                  className={`group flex min-w-0 flex-shrink-0 items-center gap-2 rounded-lg px-2 py-1.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                    isSelected ? 'bg-primary-50 ring-1 ring-inset ring-primary-200' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="relative flex-shrink-0">
                    <div
                      className={`flex h-7 w-7 items-center justify-center rounded-full border-2 transition-transform ${cfg.circle} ${
                        isSelected ? '' : 'group-hover:scale-105'
                      }`}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                        {cfg.icon}
                      </svg>
                    </div>
                    {step.accepted && (
                      <div className="absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-emerald-500 ring-2 ring-white">
                        <svg className="h-2 w-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {step.feedbackPendingRerun && !step.accepted && (
                      <div className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full bg-amber-400 ring-2 ring-white" />
                    )}
                  </div>

                  <div className="min-w-0 leading-tight">
                    <p className={`max-w-[9rem] truncate text-xs font-semibold ${isSelected ? 'text-primary-700' : 'text-slate-800'}`}>
                      {step.name}
                    </p>
                    <p className="flex items-center gap-1 whitespace-nowrap text-[10px] text-slate-400">
                      <span>Step {step.step}</span>
                      <span>·</span>
                      <span className={`font-medium ${LABEL_TEXT[step.state]}`}>
                        {step.feedbackSubmitting ? 'Submitting…' : cfg.label}
                      </span>
                      {step.outputVersion > 0 && (
                        <>
                          <span>·</span>
                          <span>v{step.outputVersion}</span>
                        </>
                      )}
                      {step.feedbackCount > 0 && (
                        <>
                          <span>·</span>
                          <span>{step.feedbackCount} feedback</span>
                        </>
                      )}
                    </p>
                  </div>
                </button>

                {!isLast && (
                  <div className="mx-1 h-0.5 w-6 flex-shrink-0 lg:min-w-[1rem] lg:flex-1">
                    <div className={`h-full w-full rounded-full ${lineColor(step.state, steps[idx + 1].state)}`} />
                  </div>
                )}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    </div>
  )
}
